import { Spacing } from '@persona-id/design-tokens';
import { useMemo, useState } from 'react';
import {
  DragDropContext,
  DraggableProvided,
  Droppable,
  DropResult,
  ResponderProvided,
} from 'react-beautiful-dnd';
import styled from 'styled-components';

import CaseStatusSticker from 'dashboard/components/CaseStatusSticker';
import ContextMenu from 'dashboard/components/ContextMenu';
import DraggableListItem from 'dashboard/components/DraggableListItem';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import ReactSelect from 'dashboard/components/ReactSelect';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { FormAddCaseStatusData } from 'dashboard/forms/FormAddCaseStatus';
import { reorder } from 'dashboard/lib/dnd/utils';
import { CaseState, generateCaseStatusToken } from 'dashboard/models/case-template';
import AddCaseStatusModal from 'dashboard/views/DashboardCaseTemplatesShowView/CaseStatusTable/AddCaseStatusModal';
import CaseStatusOption from 'dashboard/views/DashboardCaseTemplatesShowView/CaseStatusTable/CaseStatusOption';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import useConfirmation from 'lib/hooks/useConfirmation';
import useToggle from 'lib/hooks/useToggle';

const CaseStatusNameContainer = styled('div')`
  display: flex;
  column-gap: ${Spacing[4]};
`;

const TableTitleContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto auto;
  grid-column-gap: ${Spacing[4]};
`;

const StyledContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto 2fr;
  grid-column-gap: ${Spacing[1]};
  padding-left: ${Spacing[2]};
  padding-right: ${Spacing[4]};
  background-color: ${({ theme }) => theme.color.background.secondary};
  border-radius: ${Spacing[2]};
  flex-direction: row;
  align-items: center;
  width: 100%;
`;

const CaseStatusContainer = styled('div')`
  width: 100%;
  display: grid;
  padding-right: ${Spacing[6]};
  grid-template-columns: 1fr 2fr;
  grid-column-gap: ${Spacing[5]};
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const StyledHeader = styled(Label)`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: ${Spacing[3]} ${Spacing[3]} ${Spacing[3]} 6px;
  text-align: left;
`;

interface CaseStatusRowProps {
  state: CaseState;
  initialState: CaseState;
  index: number;
  allStates: CaseState[];
  editable: boolean;
  onConfirm: (states: CaseState[]) => void;
  draggableProvided: DraggableProvided;
}

const CaseStatusRow = ({
  state,
  allStates,
  initialState,
  index,
  editable,
  onConfirm,
  draggableProvided,
}: CaseStatusRowProps) => {
  const handleChangeNextStates = (values: string[]) => {
    const updatedStates = [...allStates];
    updatedStates[index] = {
      ...updatedStates[index],
      'next-state-ids': values,
    };
    onConfirm(updatedStates);
  };

  const activeStateOptions = useMemo(() => {
    const statesCopy = [...allStates];
    statesCopy.splice(index, 1);
    return statesCopy.filter((state) => state['archived-at'] == null);
  }, [allStates, index]);

  const stateOptions = useMemo(() => {
    return activeStateOptions.map((state: CaseState) => {
      return {
        label: state.name,
        value: state.token,
        backgroundColor: state.color,
      };
    });
  }, [activeStateOptions]);

  const hasError = !state['end-state'] && state['next-state-ids'].length === 0;

  const isInitialState = state.token === initialState.token;

  return (
    <CaseStatusContainer>
      <CaseStatusNameContainer>
        <CaseStatusSticker caseState={state} />
        {state['end-state'] && <Paragraph color='tertiary'>End state</Paragraph>}
        {isInitialState && <Paragraph color='tertiary'>Initial state</Paragraph>}
      </CaseStatusNameContainer>
      {/*
        Workaround to prevent react-beautiful-dnd from hijacking events from react-select
        https://github.com/atlassian/react-beautiful-dnd/issues/1810#issuecomment-1077952496
       */}
      <div
        data-rbd-drag-handle-context-id={
          draggableProvided.dragHandleProps?.['data-rbd-drag-handle-context-id']
        }
        data-rbd-drag-handle-draggable-id='ignore-drag'
        style={{
          cursor: 'auto',
        }}
      >
        <ReactSelect
          name='nextStateIds'
          id='nextStateIds'
          value={state['next-state-ids']}
          onChange={handleChangeNextStates}
          components={{ Option: CaseStatusOption }}
          hasError={hasError}
          options={stateOptions}
          isMulti={true}
          placeholder={hasError ? 'Select one or more statuses...' : 'Next Status...'}
          disabled={!editable}
        />
      </div>
    </CaseStatusContainer>
  );
};

export interface CaseStatusTableProps {
  states: CaseState[];
  editable: boolean;
  onConfirm: (states: CaseState[]) => void;
  createCaseStatusTag: (state: CaseState) => void;
}

export const CaseStatusTable = ({
  states,
  editable,
  onConfirm,
  createCaseStatusTag,
}: CaseStatusTableProps) => {
  const [addCaseStatusModal, { enable: showAddCaseStatusModal, disable: hideAddCaseStatusModal }] =
    useToggle(false);
  const [
    editCaseStatusModal,
    { enable: showEditCaseStatusModal, disable: hideEditCaseStatusModal },
  ] = useToggle(false);

  const [showDeleteCaseStatusModal, { prompt, confirm, cancel }] = useConfirmation();

  const [selectedCaseStatus, setSelectedCaseStatus] = useState<
    (CaseState & { isTheOnlyValidInitialState: boolean }) | undefined
  >(undefined);
  const [selectedCaseStatusIndex, setSelectedCaseStatusIndex] = useState<number | undefined>(
    undefined,
  );

  const handleDragEnd = (result: DropResult, _provided: ResponderProvided) => {
    const { destination, source } = result;
    if (
      !destination ||
      (destination.droppableId === source.droppableId && destination.index === source.index)
    ) {
      return;
    }
    onConfirm(reorder(states, source.index, destination.index));
  };

  const handleAddCaseStatus = (newStateFormData: FormAddCaseStatusData) => {
    const nextState: CaseState = {
      ...newStateFormData,
      'next-state-ids': [],
      token: generateCaseStatusToken(),
      'archived-at': null,
      'to-transition-rules': [],
    };

    hideAddCaseStatusModal();
    createCaseStatusTag(nextState);
    onConfirm([...states, nextState]);
  };

  const handleEditCaseStatus = (value: Partial<CaseState>) => {
    hideEditCaseStatusModal();
    const updatedState = {
      ...selectedCaseStatus,
      ...value,
    };
    const updatedStates = [...states];
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    updatedStates[selectedCaseStatusIndex] = updatedState;
    onConfirm(updatedStates);
  };

  const handleArchiveCaseStatus = (index: number) => {
    const updatedStates = [...states];
    updatedStates[index] = {
      ...updatedStates[index],
      'archived-at': new Date().toISOString(),
    };
    updatedStates.forEach((state) => {
      state['next-state-ids'] = state['next-state-ids'].filter(
        (id: string) => id !== updatedStates[index].token,
      );
    });
    onConfirm(updatedStates);
  };

  const allActiveStates = states.filter((s) => !s['archived-at']);

  const potentialInitialStates = states.filter((s) => !s['archived-at'] && !s['end-state']);

  const caseStatusRows = states.map((state: CaseState, idx: number) => {
    // skip archived case status
    if (state['archived-at']) {
      return;
    }

    const isTheOnlyValidInitialState =
      potentialInitialStates.length === 1 && state.token === potentialInitialStates[0].token;

    const handleShowEditCaseStatusModal = () => {
      setSelectedCaseStatus({ ...state, isTheOnlyValidInitialState: isTheOnlyValidInitialState });
      setSelectedCaseStatusIndex(idx);
      showEditCaseStatusModal();
    };

    const handleConfirmDeleteState = () => {
      setSelectedCaseStatus({ ...state, isTheOnlyValidInitialState: isTheOnlyValidInitialState });

      prompt(() => {
        handleArchiveCaseStatus(idx);
      });
    };

    const dropdownOptions = [
      {
        id: 'edit-status',
        label: 'Edit',
        onClick: handleShowEditCaseStatusModal,
        hidden: !editable,
      },
      {
        id: 'delete-status',
        label: 'Delete',
        onClick: handleConfirmDeleteState,
        hidden: !editable || allActiveStates.length < 3 || isTheOnlyValidInitialState,
        variant: 'danger' as const,
      },
    ];

    return (
      <DraggableListItem
        key={state.token}
        dataTest={`case-status-table__draggable-item--${state.token}`}
        draggableId={state.token}
        index={idx}
        disableDrag={!editable}
        suffix={
          <ContextMenu
            data-test={`case-status-table__status-more--${state.token}`}
            items={dropdownOptions}
            renderItem={(item) => (
              <ContextMenu.Item
                key={item.id}
                id={`case-status-table__status-${item.id}--${state.token}`}
                data-test={`case-status-table__status-${item.id}--${state.token}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  item.onClick();
                }}
                variant={item.variant}
              >
                {item.label}
              </ContextMenu.Item>
            )}
          />
        }
      >
        {(provided) => (
          <CaseStatusRow
            key={state.token}
            state={state}
            initialState={potentialInitialStates[0]}
            index={idx}
            allStates={states}
            editable={editable}
            onConfirm={onConfirm}
            draggableProvided={provided}
          />
        )}
      </DraggableListItem>
    );
  });

  return (
    <>
      <TableTitleContainer>
        <div>
          <Heading size='sm'>Statuses</Heading>
          {/* TODO(zixiao): add a link to help center article*/}
          <Paragraph>
            Customize the statuses for your cases to fit your case management process.
          </Paragraph>
        </div>
        {editable && (
          <HStack>
            <Button
              id='case-status-table__add-status'
              variant='basic'
              onClick={showAddCaseStatusModal}
              type='button'
              aria-label='Add status'
            >
              Add status
            </Button>
          </HStack>
        )}
      </TableTitleContainer>

      <div>
        <StyledContainer>
          <StyledHeader size='md'>Status</StyledHeader>
          <StyledHeader size='md'>{/* Placeholder for end-state column */}</StyledHeader>
          <StyledHeader size='md'>Next Status</StyledHeader>
        </StyledContainer>

        {caseStatusRows.length > 0 && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId='case-status-table__droppable-selected'>
              {(provided) => {
                return (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {caseStatusRows}
                    {provided.placeholder}
                  </div>
                );
              }}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      <ModalWrapper isOpen={addCaseStatusModal} handleCloseModal={hideAddCaseStatusModal}>
        <AddCaseStatusModal
          allStates={allActiveStates}
          handleClose={hideAddCaseStatusModal}
          handleConfirm={handleAddCaseStatus}
        />
      </ModalWrapper>
      <ModalWrapper isOpen={editCaseStatusModal} handleCloseModal={hideEditCaseStatusModal}>
        <AddCaseStatusModal
          editMode={true}
          state={selectedCaseStatus}
          allStates={allActiveStates}
          handleClose={hideEditCaseStatusModal}
          handleConfirm={handleEditCaseStatus}
        />
      </ModalWrapper>
      <ModalWrapper isOpen={showDeleteCaseStatusModal} handleCloseModal={cancel}>
        <ConfirmationModal
          cancelAction={cancel}
          confirmAction={confirm}
          cancelActionText='Cancel'
          confirmActionText='Delete'
          header={selectedCaseStatus && `Delete status "${selectedCaseStatus.name}"?`}
          confirmButtonType='primary'
        >
          This action is permanent and can’t be undone. Cases that are already in this state will
          remain unaffected but no more cases can be transitioned into this state. Any workflows
          using this case status will be broken.
        </ConfirmationModal>
      </ModalWrapper>
    </>
  );
};

export default CaseStatusTable;
